import { dataSourceIdMap } from "features/data-source/data-source-id-map";
import { DataSourceName, DataSourceParameterMap, UseDataSourceResult } from "features/data-source/data-source.types";
import { prepareOldApproachArgs } from "features/data-source/data-source.utils";
import { toDataSourceParameters } from "features/data-source/useDataSource";
import { useShouldUseJSBE } from "features/data-source/useShouldUseJSBE";
import { useGetDataSourceQuery as useGetDataSourceQueryJSBE } from "store/services/data-source.service";
import { useGetDataSourceQuery as useGetDataSourceQueryCSBE } from "store/services/deprecated/data-source.service";

/**
 * Temporary migration hook for fetching data sources using the old approach.
 *
 * This hook determines whether to use the JS Backend (JSBE) or the legacy C# Backend (CSBE)
 * based on feature flags and data source type. The switching logic is handled by the
 * `useShouldUseJSBE` hook, which checks user area permissions and data source mappings.
 *
 * - If JSBE is enabled and the data source is not a plugin, the JSBE query is used.
 * - Otherwise, the legacy C# BE query is used.
 *
 * This hook is intended only for the migration period and should be removed once
 * all data sources are migrated to the new approach.
 *
 * @param dataSource The name of the data source to query.
 * @param parameters Optional parameters for the data source.
 * @param options Optional settings, e.g., skip.
 * @returns The result of the data source query.
 */
export const useDataSourceOldApproach = <T = any>(
    dataSource: DataSourceName,
    parameters?: DataSourceParameterMap,
    options?: { skip?: boolean }
): UseDataSourceResult<T> => {
    const { useJSBE } = useShouldUseJSBE(dataSource);

    const argsJSBE = prepareOldApproachArgs(dataSource, parameters, options);
    const argsCSBE = {
        id: dataSourceIdMap[dataSource],
        parameters: toDataSourceParameters(parameters),
    };

    const resultJSBE = useGetDataSourceQueryJSBE(argsJSBE, { skip: !useJSBE || options?.skip });
    const resultCSBE = useGetDataSourceQueryCSBE(argsCSBE, { skip: useJSBE || options?.skip });
    const result = useJSBE ? resultJSBE : resultCSBE;

    return result as UseDataSourceResult<T>;
};
